import React, { useEffect, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { Drawer } from '../../../../shared/drawer/drawer'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { IconRedo } from '../../../../shared/icon/icon'
import { useRoleSysRequestLog } from '../../../../role/hook/role-sys'
import { useAlertRoutingAccess } from './access'
import { getRun, listTargets, resolveRun, retryRun } from './alert-routing-api'
import { useCatalog } from './catalog'
import { ALERT_STATUS, DELIVERY_STATUS, SEVERITY, WordPill } from './run-words'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { RUNS_PAGE, routeHref } from './paths'
import { Ago, DrawerSection } from './drawer-parts'
import { renderTemplate } from './render-template'
import { whereOf } from './target-words'

// What happened on each matched route (staff_alert_matches.outcome).
const outcomeWords = m => ({
  queued: 'sent now',
  held_paused: 'held while the route is paused',
  skipped_schedule: 'skipped (outside the schedule)',
  expired: 'expired before it could be sent',
  held_schedule: `held until ${m.heldUntil ? formatDateTime(m.heldUntil) : 'the schedule opens'}`,
  digested: m.digestID ? `in digest #${m.digestID}` : 'in a digest'
}[m.outcome] || m.outcome)

// An attempt's outcome as a pill (staff_alert_attempts.outcome).
const ATTEMPT_OUTCOME = {
  sent: { label: 'Sent', tone: 'ok' },
  retry: { label: 'Will retry', tone: 'warn' },
  permanent: { label: 'Failed', tone: 'bad' },
  failed: { label: 'Failed', tone: 'bad' },
  expired: { label: 'Expired', tone: 'bad' }
}

// The test a target's Send test raises: the catalogue leaves it out (no
// route selects it), so its words are kept here, as core registers them.
export const TEST_TRIGGER = {
  kind: 'routing.test_target', label: 'Test message', mapping: { title: 'Test from EducationXR alert routing' },
  schema: [{ key: 'target_name', label: 'Target', class: 'operational' }, { key: 'sent_by', label: 'Sent by', class: 'personal' }]
}

const parsePayload = text => {
  try {
    const v = JSON.parse(text || '{}')
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {}
  } catch (e) {
    return {}
  }
}

const show = v => (v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v))
// A key the catalogue doesn't name, in words: "target_name" is "Target name".
export const humanize = key => {
  const words = String(key).replace(/[_.-]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key
}

// The alert's own title, as its message said it: the server's, else the
// trigger's title template over its payload (render-template.js), else the
// trigger's label.
export const alertTitle = (alert, trigger, payload) => {
  if (alert.title) return alert.title
  const tpl = trigger.mapping && trigger.mapping.title
  const ctx = { payload, alert: { kind: trigger.kind, label: trigger.label, category: alert.category, severity: alert.severity, raised_at: alert.raisedAt, environment: '' } }
  return (tpl && renderTemplate(tpl, ctx).trim()) || trigger.label || alert.trigger
}

// Where a delivery went, in words, never "target:7": an email delivery's own
// address, else its target's channel, address count or webhook host.
const deliveryWhere = (d, targets) => {
  if (d.destination && !/^target:/.test(d.destination)) return d.destination
  return whereOf(targets[d.targetID])
}

// One alert (spec §20.4), in the house drawer (owner 2026-10-30: "reads like
// a debug dump"): its title as the message said it, its trigger, severity and
// status as pills under it (the raw kind only on hover); Retry now pinned in
// the head. Raised when, by whom; Resolve with what it does beside it (never
// for a test). Then what it carried, by the trigger's field labels; what each
// matched route did with it; and each delivery: the target and where it went,
// its attempts as a short timeline (when, result, how long; the server and
// the provider's answer on hover).
export const RunDrawer = () => {
  const { alertID } = useParams()
  const history = useHistory()
  const location = useLocation()
  const catalog = useCatalog()
  const { canEdit } = useAlertRoutingAccess()
  // Opened from a target's or a workspace's page (their rows carry returnTo):
  // closing goes back there, else to Alert runs.
  const [returnTo] = useState(() => (location.state && location.state.returnTo) || RUNS_PAGE)
  const canOpenRequest = Boolean(useRoleSysRequestLog().accessRead)
  const [run, setRun] = useState(null)
  const [targets, setTargets] = useState({})
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  useEffect(() => { getRun(alertID).then(setRun, err => setError(serverMessage(err, "This alert couldn't be loaded."), err)) }, [alertID, setError])
  // the targets, for where each delivery went (a channel, a count, a host)
  useEffect(() => {
    let live = true
    Promise.resolve(listTargets()).then(list => live && setTargets(Object.fromEntries((list || []).map(t => [t.id, t]))), () => {})
    return () => { live = false }
  }, [])
  const close = () => history.push(returnTo)
  const act = async fn => {
    if (busy) return
    setBusy(true)
    try { setError(null); setRun(await fn()) } catch (err) { setError(serverMessage(err, "That didn't work."), err) } finally { setBusy(false) }
  }
  if (!run) return <Drawer open title="Alert" onClose={close}>{error ? <><p className="text-danger small" role="alert">{error}</p><ErrorReference error={failure} /></> : <p className="sw-hint">Loading…</p>}</Drawer>

  const a = run.alert
  const trigger = catalog.triggers.find(t => t.kind === a.trigger) || (a.trigger === TEST_TRIGGER.kind ? TEST_TRIGGER : { kind: a.trigger, label: humanize(a.trigger), schema: [] })
  const schema = trigger.schema || []
  const payload = parsePayload(a.payload)
  const known = schema.filter(f => payload[f.key] !== undefined)
  const unknown = Object.keys(payload).filter(k => !schema.some(f => f.key === k))
  const deliveries = run.deliveries || []
  const matches = run.matches || []
  const retryable = deliveries.some(d => d.status === 'failed' || d.status === 'expired')

  const headMenu = canEdit && retryable &&
    <Menu><MenuItem Icon={IconRedo} text="Retry now" pin show disabled={busy} onClick={() => act(() => retryRun(a.id))} /></Menu>
  const subtitle =
    <span className="ar-drawer-sub">
      <span title={a.trigger}><Pill tone="info">{trigger.label}</Pill></span>
      <WordPill words={SEVERITY} value={a.severity} />
      <WordPill words={ALERT_STATUS} value={a.status} />
      {a.isTest && <Pill tone="info">Test</Pill>}
      {a.usedBackstop && <Pill tone="warn">Sent to the backstop</Pill>}
    </span>

  return (
    <Drawer open size="lg" title={alertTitle(a, trigger, payload)} subtitle={subtitle} actions={headMenu} onClose={close}>
      <div className="ar-drawer">
        {error && <div role="alert"><p className="text-danger small m-0">{error}</p><ErrorReference error={failure} compact /></div>}
        <p className="m-0">
          Raised <Ago at={a.raisedAt} /> by {a.actorLabel || a.actorKind || 'the system'}
          {a.requestID && canOpenRequest && <> · <Link to={`/admin/request-log/${a.requestID}?at=${encodeURIComponent(a.raisedAt)}`}>Open request</Link></>}
        </p>
        {a.resolvedAt && <p className="sw-hint m-0">Resolved <Ago at={a.resolvedAt} />{a.resolvedBy ? ` by ${a.resolvedBy}` : ''}</p>}
        {canEdit && !a.isTest && !a.resolvedAt &&
          <div className="ar-row">
            <button type="button" className="btn sw-btn btn-sm" disabled={busy} aria-describedby="ar-resolve-help" onClick={() => act(() => resolveRun(a.id))}>Resolve</button>
            <span className="sw-hint" id="ar-resolve-help">Mark as handled. Doesn't change what was sent.</span>
          </div>}
        {!canEdit && retryable && <Notice tone="warn">Retrying needs the alert routing update permission, and can't be done while signed in as someone else.</Notice>}

        <div>
          <DrawerSection title="What it carried">
            {known.length + unknown.length === 0
              ? <p className="sw-hint m-0">This alert carried nothing.</p>
              : <dl className="sw-dl">
                {known.map(f => (
                  <React.Fragment key={f.key}>
                    <dt title={f.class ? `${f.label}: ${f.class} data` : undefined}>{f.label}{f.class === 'personal' && <> <Pill tone="warn">Personal</Pill></>}</dt>
                    <dd>{show(payload[f.key])}</dd>
                  </React.Fragment>))}
                {unknown.map(k => <React.Fragment key={k}><dt title={k}>{humanize(k)}</dt><dd>{show(payload[k])}</dd></React.Fragment>)}
              </dl>}
          </DrawerSection>

          <DrawerSection title="Routes">
            {a.isTest
              ? <p className="sw-hint m-0">Test message: sent only to the target being tested.</p>
              : matches.length === 0
                ? <p className="sw-hint m-0">No route matched{a.usedBackstop ? ', so it went to the backstop' : ''}.</p>
                : <ul className="ar-plain-list">{matches.map((m, i) => <li key={i}><Link to={routeHref(m.routeID)}>{m.routeName}</Link>: {outcomeWords(m)}</li>)}</ul>}
          </DrawerSection>

          <DrawerSection title="Deliveries">
            {deliveries.length === 0 && <p className="sw-hint m-0">Nothing has been sent yet.</p>}
            <div className="d-grid gap-2">
              {deliveries.map(d => {
                const where = deliveryWhere(d, targets)
                const last = (d.history || [])[0]
                const repeated = last && last.error && last.error === d.lastError
                return (
                  <div key={d.id} className="ar-card ar-delivery" title={d.providerMessageID ? `${d.channel || 'Delivery'} message id ${d.providerMessageID}` : undefined}>
                    <div className="ar-pills">
                      <strong>{d.targetName || d.via}</strong>
                      {where && <span className="sw-hint">{where}</span>}
                      <WordPill words={DELIVERY_STATUS} value={d.status} />
                    </div>
                    <div className="sw-hint small">
                      {plural(d.attempts || 0, 'attempt')}
                      {d.status === 'pending' && d.nextAttemptAt && <> · next <Ago at={d.nextAttemptAt} /></>}
                      {d.digestID && <> · in a digest shared by every alert in its window</>}
                    </div>
                    {d.lastError && !repeated && <div className="sw-tone-bad small">{d.lastError}</div>}
                    {(d.history || []).length > 0 &&
                      <ol className="ar-timeline ar-attempts">{d.history.map((h, i) => {
                        const outcome = ATTEMPT_OUTCOME[h.outcome] || { label: h.outcome, tone: null }
                        const detail = [h.providerStatus && `Answer ${h.providerStatus}`, h.host && `Server ${h.host}`].filter(Boolean).join(' · ')
                        return (
                          <li key={i} title={detail || undefined}>
                            <span>{formatDateTime(h.attemptedAt)}</span>
                            <Pill tone={outcome.tone}>{outcome.label}</Pill>
                            {h.durationMs !== undefined && h.durationMs !== null && <span>{`${h.durationMs} ms`}</span>}
                            {h.error && <span className="sw-tone-bad">{h.error}</span>}
                          </li>
                        )
                      })}</ol>}
                    {d.routeID && !a.isTest && <Link className="small" to={routeHref(d.routeID)}>Open route</Link>}
                  </div>
                )
              })}
            </div>
          </DrawerSection>
        </div>
      </div>
    </Drawer>
  )
}
